import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDataKeysAtTime, useDataSnapshots } from '../../hooks/useDataKeys'
import { useDataRestore } from '../../hooks/useDataHistory'
import { useMe } from '../../hooks/useMe'
import { Card } from '../../components/ui/Card'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { ErrorBanner } from '../../components/ErrorBanner'
import { VersionPicker } from '../../components/VersionPicker'
import { apiErrorMessage } from '../../api/client'
import { formatBytes } from '../../lib/format'
import type { DataKey, Glow } from '../../api/types'

// DataTab is the Phase 11.6 follow-up to the original HistoryTab.
// Primary view: a directory-style listing of every live key under the
// glow's shared `data.*` surface, with file sizes. A VersionPicker
// pill in the toolbar lets the user time-travel the whole listing to
// any per-minute bucketed snapshot in retention.
//
// Scope: data.* only. userdata + capture stay out of the v1
// management API surface (CLAUDE.md §9; ROADMAP Phase 11.6 deferred
// item).
//
// Restore semantics: when the picker is on a historical snapshot,
// each file row gets a "Restore" button that flips that one key's
// live version to its-state-in-this-snapshot. The live view hides
// the button (there's nothing to restore against the current state).
export function DataTab({ glow }: { glow: Glow }) {
  const [selectedBucketAt, setSelectedBucketAt] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<DataKey | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  const me = useMe()
  const isPaid =
    me.data?.subscription.status === 'active' ||
    me.data?.subscription.status === 'trialing'

  const snapshots = useDataSnapshots(glow.id)
  const keys = useDataKeysAtTime(glow.id, selectedBucketAt)
  const restore = useDataRestore(glow.id)

  const isLive = selectedBucketAt === null

  const doRestore = async (k: DataKey) => {
    setRestoreError(null)
    try {
      await restore.mutateAsync({ key: k.path, generation: k.generation })
      setConfirm(null)
    } catch (e) {
      setRestoreError(apiErrorMessage(e, 'Restore failed.'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-medium text-cream">Data</h2>
          <p className="mt-0.5 text-sm text-muted">
            Everything saved to{' '}
            <code className="font-mono text-xs">window.backlit.data</code> on
            this glow.{' '}
            <code className="font-mono text-xs">userdata</code> and{' '}
            <code className="font-mono text-xs">capture</code> aren't shown
            here in v1.
          </p>
        </div>
        <VersionPicker
          snapshots={snapshots.data?.snapshots ?? []}
          selectedBucketAt={selectedBucketAt}
          onChange={setSelectedBucketAt}
        />
      </div>

      <DataKeysListContent
        keys={keys}
        isLive={isLive}
        onRestore={(k) => {
          setRestoreError(null)
          setConfirm(k)
        }}
        isPaid={isPaid}
      />

      {confirm && (
        <ConfirmRestoreDialog
          dataKey={confirm}
          onCancel={() => {
            setConfirm(null)
            setRestoreError(null)
          }}
          onConfirm={() => doRestore(confirm)}
          busy={restore.isPending}
          error={restoreError}
        />
      )}
    </div>
  )
}

function DataKeysListContent({
  keys,
  isLive,
  onRestore,
  isPaid,
}: {
  keys: ReturnType<typeof useDataKeysAtTime>
  isLive: boolean
  onRestore: (k: DataKey) => void
  isPaid: boolean
}) {
  if (keys.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (keys.error) {
    return <ErrorBanner>{(keys.error as Error).message}</ErrorBanner>
  }
  const data = keys.data?.keys ?? []
  if (data.length === 0) {
    return (
      <Card>
        <h3 className="font-medium">
          {isLive ? 'No data yet' : 'No data at this snapshot'}
        </h3>
        <p className="mt-1 text-sm text-cream/70">
          {isLive
            ? 'Apps deployed to this glow can write to window.backlit.data from any page; what they save shows up here.'
            : 'Nothing was live at the moment you picked. Try a more recent snapshot in the picker.'}
        </p>
        {!isPaid && isLive && (
          <p className="mt-3 text-sm text-cream/70">
            Free-tier glows keep <strong>24 hours</strong> of version history.{' '}
            <Link to="/billing" className="font-medium text-glow-600 hover:underline">
              Upgrade to Beam
            </Link>{' '}
            to keep 30 days.
          </p>
        )}
      </Card>
    )
  }
  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider text-muted">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2 text-right">Size</th>
              <th className="px-4 py-2 text-right">Modified</th>
              <th className="px-4 py-2 text-right" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.map((k) => (
              <tr key={k.path}>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2.5">
                    <FileIcon path={k.path} />
                    <span className="font-mono text-[12.5px] text-cream">
                      {k.path}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-cream/70">
                  {formatBytes(k.size)}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[11.5px] text-muted">
                  {shortTime(k.time_created)}
                </td>
                <td className="px-4 py-3 text-right">
                  {!isLive && (
                    <Button variant="secondary" onClick={() => onRestore(k)}>
                      Restore
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!isPaid && (
        <div className="border-t border-line bg-surface-2 px-4 py-3 text-xs text-cream/70">
          Free-tier history is capped at 24 hours.{' '}
          <Link to="/billing" className="font-medium text-glow-600 hover:underline">
            Upgrade to Beam
          </Link>{' '}
          for 30 days.
        </div>
      )}
    </Card>
  )
}

function ConfirmRestoreDialog({
  dataKey,
  onCancel,
  onConfirm,
  busy,
  error,
}: {
  dataKey: DataKey
  onCancel: () => void
  onConfirm: () => void
  busy: boolean
  error: string | null
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold">Restore this key?</h3>
        <p className="mt-2 text-sm text-cream/70">
          The current live value of{' '}
          <code className="rounded bg-surface-2 px-1 font-mono text-xs">
            {dataKey.path}
          </code>{' '}
          will be replaced with the {formatBytes(dataKey.size)} version from{' '}
          {fmtDate(dataKey.time_created)}. The previously-live version stays
          recoverable from the picker.
        </p>
        {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? 'Restoring…' : 'Restore'}
          </Button>
        </div>
      </Card>
    </div>
  )
}

// FileIcon renders a small 2-letter monogram tinted by file extension —
// mirrors the design handoff's FileIcon helper but mapped to Tailwind
// palette so we don't ship inline hex codes.
function FileIcon({ path }: { path: string }) {
  const ext = (path.split('.').pop() ?? '').toLowerCase()
  const palette: Record<string, string> = {
    html: 'text-orange-600 bg-orange-500/10 ring-orange-100',
    htm: 'text-orange-600 bg-orange-500/10 ring-orange-100',
    css: 'text-blue-600 bg-blue-500/10 ring-blue-100',
    js: 'text-yellow-300 bg-yellow-500/10 ring-yellow-100',
    ts: 'text-blue-300 bg-blue-500/10 ring-blue-100',
    json: 'text-yellow-300 bg-yellow-500/10 ring-yellow-100',
    md: 'text-cream/70 bg-surface-2 ring-line',
    png: 'text-violet-600 bg-violet-50 ring-violet-100',
    jpg: 'text-violet-600 bg-violet-50 ring-violet-100',
    jpeg: 'text-violet-600 bg-violet-50 ring-violet-100',
    svg: 'text-violet-600 bg-violet-50 ring-violet-100',
    gif: 'text-violet-600 bg-violet-50 ring-violet-100',
    webp: 'text-violet-600 bg-violet-50 ring-violet-100',
  }
  const cls = palette[ext] ?? 'text-muted bg-surface-2 ring-line'
  return (
    <span
      className={[
        'inline-flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded ring-1',
        'font-mono text-[9px] font-semibold tracking-tight',
        cls,
      ].join(' ')}
      aria-hidden="true"
    >
      {ext.slice(0, 2).toUpperCase() || '··'}
    </span>
  )
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function shortTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return iso
  }
}
